import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import css from './Sections.module.css';
import { isArrayLength } from '../../../util/genericHelpers';
import { Button, H3, Icons } from '../../../components';
import {
  getYachtClubOptionLabel,
  getYachtClubSearchText,
  normalizeYachtClubAffiliations,
  resolveYachtClubOption,
  useBoatCatalog,
} from '../../../util/boatCatalog';
import { DEFAULT_YACHT_CLUB_FLAG_URL } from '../../../util/boatAssetMap';

const MAX_YACHT_CLUBS = 3;
const MAX_YACHT_CLUB_SUGGESTIONS = 30;

const handleFlagError = event => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = DEFAULT_YACHT_CLUB_FLAG_URL;
};

const YachtClubInlinePicker = props => {
  const { intl, options, selectedClubIds, onAdd, disabled } = props;
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const normalizedQuery = query.trim().toLowerCase();
  const selectedOption = options.find(option => getYachtClubOptionLabel(option) === query);

  const suggestions = useMemo(() => {
    const availableOptions = (options || []).filter(
      option => !selectedClubIds.includes(option.key)
    );
    const matchedOptions = normalizedQuery
      ? availableOptions.filter(option => getYachtClubSearchText(option).includes(normalizedQuery))
      : availableOptions;

    return matchedOptions.slice(0, MAX_YACHT_CLUB_SUGGESTIONS);
  }, [normalizedQuery, options, selectedClubIds]);

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(0);
    }
  }, [isOpen]);

  const selectClub = club => {
    setQuery(getYachtClubOptionLabel(club));
    setIsOpen(false);
  };

  const addClub = () => {
    const club = selectedOption;
    if (!club?.key || selectedClubIds.includes(club.key)) return;
    onAdd(club.key);
    setQuery('');
    setIsOpen(false);
  };

  const handleKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(index => Math.min(index + 1, Math.max(suggestions.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && isOpen && suggestions[highlightedIndex]) {
      event.preventDefault();
      selectClub(suggestions[highlightedIndex]);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      addClub();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const canAdd = !!selectedOption?.key && !disabled;

  return (
    <div className={css.yachtClubInlinePicker}>
      <label htmlFor="profile-yacht-club-search">
        {intl.formatMessage({ id: 'ProfilePage.SectionYachtClubAffiliations.addLabel' })}
      </label>
      <div className={css.yachtClubPickerRow}>
        <div className={css.yachtClubAutocomplete}>
          <input
            id="profile-yacht-club-search"
            type="text"
            role="combobox"
            aria-expanded={isOpen}
            autoComplete="off"
            value={query}
            placeholder={intl.formatMessage({
              id: 'ProfilePage.SectionYachtClubAffiliations.addPlaceholder',
            })}
            onChange={event => {
              setQuery(event.target.value);
              setIsOpen(true);
              setHighlightedIndex(0);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
          {isOpen && suggestions.length > 0 ? (
            <ul className={css.yachtClubSuggestions}>
              {suggestions.map((club, index) => {
                const label = getYachtClubOptionLabel(club);
                return (
                  <li
                    key={club.key}
                    className={classNames({
                      [css.yachtClubSuggestionHighlighted]: highlightedIndex === index,
                    })}
                    onMouseDown={event => {
                      event.preventDefault();
                      selectClub(club);
                    }}
                  >
                    <img src={club.flagUrl} alt="" onError={handleFlagError} />
                    <span>{label}</span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        <Button
          type="button"
          className={classNames(css.inlineActionButton, css.yachtClubAddButton)}
          onClick={addClub}
          disabled={!canAdd}
        >
          {intl.formatMessage({ id: 'ProfilePage.SectionYachtClubAffiliations.addButton' })}
        </Button>
      </div>
    </div>
  );
};

const SectionYachtClubAffiliations = props => {
  const { publicData, intl, editable = false, onUpdateProfile, updateInProgress = false } = props;
  const yachtClubs = publicData?.yachtClubs;
  const { yachtClubs: availableYachtClubs } = useBoatCatalog();
  const [localError, setLocalError] = useState(null);

  const normalizedAffiliations = normalizeYachtClubAffiliations(yachtClubs);
  const affiliatedClubs = normalizedAffiliations
    .map(item => resolveYachtClubOption(availableYachtClubs, item?.clubId))
    .filter(club => club && !club?.disabled);
  const selectedClubIds = affiliatedClubs.map(club => club.key);
  const canAddMore = affiliatedClubs.length < MAX_YACHT_CLUBS;

  const updateYachtClubs = async nextClubs => {
    setLocalError(null);
    try {
      await onUpdateProfile({
        publicData: {
          yachtClubs: normalizeYachtClubAffiliations(nextClubs),
        },
      });
    } catch (e) {
      setLocalError(e);
    }
  };

  const addYachtClub = clubId => {
    if (!clubId || selectedClubIds.includes(clubId) || !canAddMore) return;
    updateYachtClubs([...normalizedAffiliations, { clubId }]);
  };

  const removeYachtClub = clubId => {
    updateYachtClubs(normalizedAffiliations.filter(item => item.clubId !== clubId));
  };

  return (
    <div className={css.yachtClubSection}>
      <H3 className={css.captainsLogTitle}>
        <Icons name="users" />{' '}
        {intl.formatMessage({ id: 'ProfilePage.SectionYachtClubAffiliations.heading' })}
      </H3>

      {isArrayLength(affiliatedClubs) ? (
        <div className={css.clubList}>
          {affiliatedClubs.map((club, index) => {
            const label = getYachtClubOptionLabel(club);
            const title = club.websiteUrl ? (
              <a
                className={css.clubTitle}
                href={club.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {label}
              </a>
            ) : (
              <span className={css.clubTitle}>{label}</span>
            );

            return (
              <div key={`${club.key}-${index}`} className={css.clubItem}>
                <div className={css.clubImg}>
                  <img
                    src={club.flagUrl}
                    alt={label}
                    className={css.flag}
                    width={50}
                    height={30}
                    onError={handleFlagError}
                  />
                </div>

                <div className={css.clubInfo}>
                  {title}
                  {editable ? (
                    <button
                      type="button"
                      className={css.removeYachtClubInline}
                      onClick={() => removeYachtClub(club.key)}
                      disabled={updateInProgress}
                    >
                      {intl.formatMessage({
                        id: 'ProfilePage.SectionYachtClubAffiliations.removeButton',
                      })}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className={css.emptyState}>
          {intl.formatMessage({
            id: editable
              ? 'ProfilePage.SectionYachtClubAffiliations.noAffiliationsEditable'
              : 'ProfilePage.SectionYachtClubAffiliations.noAffiliations',
          })}
        </p>
      )}

      {editable && canAddMore ? (
        <YachtClubInlinePicker
          intl={intl}
          options={availableYachtClubs}
          selectedClubIds={selectedClubIds}
          onAdd={addYachtClub}
          disabled={updateInProgress}
        />
      ) : null}
      {localError ? (
        <p className={css.inlineError}>
          {intl.formatMessage({ id: 'ProfileSettingsForm.updateProfileFailed' })}
        </p>
      ) : null}
    </div>
  );
};

export default SectionYachtClubAffiliations;
